import { Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";

// THE shared −/value/+ stepper. Used by the program editor to edit cycleWeeks in
// place (PE-11): one definition so the create-time and edit-time steppers can't drift on
// sizing, disabled treatment, or the ≥44 touch target. a11y: each button spells
// its action ("Weeks +") and disables at the bound; the value is tabular-nums.

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

export type StepperProps = {
  theme: UiTheme;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  /** How much one tap moves. Default 1. */
  step?: number;
  /** The value as shown (a decimal comma, a unit). Default String. */
  format?: (v: number) => string;
};

export function Stepper({ theme, label, value, min, max, onChange, step = 1, format = String }: StepperProps) {
  const styles = makeStyles(theme);
  // Rounded to the step's own decimals: 0.1 + 0.2 never shows.
  const move = (d: number) => onChange(clamp(Math.round((value + d) * 1000) / 1000, min, max));
  return (
    <View style={styles.line}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.controls}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${label} −`}
          disabled={value <= min}
          onPress={() => move(-step)}
          style={[styles.btn, value <= min && styles.btnDisabled]}
        >
          <Text style={styles.btnText}>−</Text>
        </Pressable>
        <Text style={styles.value}>{format(value)}</Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${label} +`}
          disabled={value >= max}
          onPress={() => move(step)}
          style={[styles.btn, value >= max && styles.btnDisabled]}
        >
          <Text style={styles.btnText}>＋</Text>
        </Pressable>
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    line: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      // At a large font a wide value («RPE 8») leaves no room for the label
      // beside the controls: the controls drop under it instead of off the screen.
      flexWrap: "wrap",
      rowGap: s.s1,
      minHeight: 48,
    },
    label: { flexShrink: 1, color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    controls: { flexDirection: "row", alignItems: "center", gap: s.s4, flexShrink: 0, marginLeft: "auto" },
    btn: {
      width: 48,
      height: 44,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    btnDisabled: { opacity: 0.35 },
    btnText: { color: p.ink, fontSize: t.sizes.lg, fontWeight: "700" },
    value: {
      minWidth: 36,
      textAlign: "center",
      color: p.ink,
      fontSize: t.sizes.lg,
      fontFamily: t.fonts.mono,
      fontWeight: "800",
      fontVariant: ["tabular-nums"],
    },
  });
}
